import { useCallback, useEffect, useState } from 'react';
import { logger } from '../lib/logger';

export interface EmailContext {
  subject: string;
  body: string;
  senderEmail: string;
  senderName: string;
  messageId?: string;
  receivedAt?: string;
}

// Browser preview mock: when loaded outside Outlook (plain browser for UI dev),
// Office.context.mailbox is undefined. Return a sample email so the UI renders.
const BROWSER_PREVIEW_CONTEXT: EmailContext = {
  subject: '[Preview] Ski package booking for next weekend',
  body: 'Hi, I would like to book the full ski package for 2-3 February. Is it available for 3 adults? Thanks, Lars',
  senderEmail: 'lars.hansen@example.com',
  senderName: 'Lars Hansen',
  messageId: 'outlook-preview:ski-package-booking',
  receivedAt: new Date().toISOString(),
};

function toIsoDate(value: unknown): string | undefined {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

// Stable, cross-host key for a message. `item.itemId` (the EWS ID) is returned
// in DIFFERENT formats across Outlook clients for the SAME email — desktop/new
// Outlook give an EWS-format ID, while Outlook on the web and mobile give a
// REST-style encoding. That mismatch means a draft saved on the phone can't be
// found from the desktop (and vice versa), because draft history is looked up
// by exact key. `internetMessageId` is the RFC 5322 Message-ID header — globally
// unique and identical on every client — so it recovers drafts across devices.
// (convertToRestId/convertToEwsId would normalize itemId but are unsupported on
// Outlook mobile, so they can't be used here.) itemId remains a last-resort
// fallback for the rare host that doesn't surface internetMessageId in read mode.
export function stableMessageKey(item: {
  internetMessageId?: string | null;
  itemId?: string | null;
}): string | undefined {
  const internetId = item.internetMessageId?.trim();
  if (internetId) return `outlook-internet-message:${internetId}`;
  if (item.itemId) return `outlook-message:${item.itemId}`;
  return undefined;
}

function isInOfficeHost(): boolean {
  return (
    typeof Office !== 'undefined' &&
    Office.context !== undefined &&
    Office.context.mailbox !== undefined
  );
}

// Office.js's ItemChanged event is well documented as unreliable specifically
// on Outlook on the web / New Outlook (works fine in classic desktop Outlook) —
// see https://github.com/OfficeDev/office-js/issues/6101 and
// https://learn.microsoft.com/en-us/answers/questions/5537293. When it silently
// fails to fire, the pane has no other signal that the selection changed, so it
// freezes on the previous email with no error. This poll is the backstop.
const ITEM_CHANGE_POLL_MS = 1500;

/**
 * Strip client-injected noise from a plain-text email body so the preview — and
 * the draft the backend generates — see the real message only.
 *
 * The big offender is Microsoft's "first contact" external-sender safety tip
 * ("You don't often get email from x@y. Learn why this is important" + an
 * aka.ms link) that Exchange Online prepends to the body for unfamiliar senders.
 */
export function cleanEmailBody(raw: string): string {
  return (
    raw
      .replace(/\r\n|\r|\n/g, '\n')
      // Microsoft's external-sender "first contact" safety tip. The sentence and
      // its "Learn why this is important" link often sit on SEPARATE lines, and
      // Text coercion can trail leaked CSS from the banner's <style> block
      // (e.g. "<background:revert!important; …>"). Match across newlines (bounded
      // so real content is never eaten) and swallow an optional trailing <…> blob.
      .replace(
        /You don[’']t often get email from[\s\S]{0,300}?Learn why this is important\s*(?:<[\s\S]{0,400}?>)?/gi,
        '',
      )
      // Any bare aka.ms link left behind.
      .replace(/<?https?:\/\/aka\.ms\/LearnAboutSenderIdentification>?/gi, '')
      // Leaked CSS blobs wrapped in angle brackets (banner <style> leakage).
      .replace(
        /<[^<>]*(?:!important|revert|background:|font-|margin:|padding:|color:)[^<>]*>/gi,
        '',
      )
      // Auto-appended legal/confidentiality footer (always the trailing block).
      // Strip from the first boilerplate trigger to the end — never real content.
      .replace(
        /\n*\s*["“']?\s*(?:This (?:e-?mail|message|communication|transmission)\b[^\n]*?\bconfidential|CONFIDENTIALITY(?:\s+NOTICE)?|The information (?:contained )?in this (?:e-?mail|message)|If you are not the intended recipient|This (?:e-?mail|message) and any (?:files|attachments))[\s\S]*$/i,
        '',
      )
      // Collapse the blank lines the removals leave behind, then trim.
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}

/**
 * Convert an HTML email body to plain text, inserting a newline at each
 * block boundary first.
 *
 * `Office.CoercionType.Text` silently drops the separator between
 * table/div-based blocks for some signatures (common in HTML email
 * signatures built as a table), gluing lines like "policy?" and "Cristian"
 * together with no space at all. Coercing to HTML and inserting our own
 * newlines before stripping tags avoids relying on that coercion.
 */
export function htmlBodyToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const br of Array.from(doc.body.querySelectorAll('br'))) {
    br.replaceWith('\n');
  }
  for (const block of Array.from(
    doc.body.querySelectorAll('p, div, tr, li, table, h1, h2, h3, h4, h5, h6'),
  )) {
    block.append('\n');
  }
  return doc.body.textContent ?? '';
}

/**
 * Flatten a body for the clamped "Replying to" snippet.
 *
 * `-webkit-line-clamp` uses `display: -webkit-box`, which drops EOL characters
 * instead of treating them as spaces — signature lines then run together
 * (`policy?Alex`). Replace line breaks with a space, then truncate.
 */
export function previewEmailBody(body: string, maxLength = 200): string {
  const flattened = body
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();
  if (flattened.length <= maxLength) return flattened;
  return `${flattened.slice(0, maxLength)}…`;
}

export function useEmailContext() {
  const [context, setContext] = useState<EmailContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Confirmed on outlook.live.com (Outlook.com/consumer accounts):
  // Office.context.mailbox.item.itemId never updates for the life of the
  // pane, even after the user switches to an unrelated email — ItemChanged
  // doesn't fire and polling has nothing to detect. Office.js has no working
  // API to force a live re-sync of mailbox.item on that host; a matching
  // report (https://github.com/OfficeDev/office-js/issues/5827) confirms the
  // only known workaround is a full reload, which re-triggers the same
  // fresh-open read that already works correctly on every host. In-progress
  // drafts aren't lost — they're persisted to localStorage keyed by
  // messageId (see lib/taskpaneSnapshot.ts) and recoverable via Previous
  // Drafts after the reload lands on the newly selected email.
  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (!isInOfficeHost()) {
      // Browser-only preview mode — no Office.js available
      setContext(BROWSER_PREVIEW_CONTEXT);
      setLoading(false);
      return;
    }

    let cancelled = false;
    // Bumped on every loadItem() call. ItemChanged can fire again before a
    // prior body.getAsync callback resolves (fast clicking through the list),
    // and Office gives no ordering guarantee on those callbacks — so a stale
    // one could otherwise overwrite the newer item's context. Each callback
    // only applies its result if it still owns the latest token.
    let requestToken = 0;
    // Raw Office itemId of the last item loadItem() started reading, used by
    // the poll below to detect a selection change without waiting on the
    // (possibly never-firing) ItemChanged event.
    let lastKnownItemId: string | null = null;
    // Confirmed on outlook.live.com (ticket 1217316409090454): the entire
    // mailbox.item object can freeze for the pane's lifetime, not just
    // itemId — subject stays stale too. Tracking both here so the poll
    // below catches a host that only freezes one of the two.
    let lastKnownSubject: string | null = null;

    // The task pane runs in the Shared Runtime, so it stays alive as the user
    // clicks between messages — Office does not remount/reload it. Without the
    // ItemChanged subscription below this only ever ran once, on the item that
    // was selected when the pane first loaded, leaving the sidebar stuck on it.
    //
    // loadItem() takes a `trigger` label purely to log which signal invoked
    // it — useful when diagnosing which of these paths (or none) actually
    // fires on a given Outlook host.
    function loadItem(trigger: 'initial' | 'itemChanged-event' | 'poll' | 'visibility') {
      const token = ++requestToken;
      const item = Office.context.mailbox.item;
      const previousItemId = lastKnownItemId;
      lastKnownItemId = item?.itemId ?? null;
      lastKnownSubject = item?.subject ?? null;
      logger.info('[email-context] loadItem', {
        trigger,
        itemId: item?.itemId ?? null,
        previousItemId,
        subject: item?.subject ?? null,
      });
      if (!item) {
        if (cancelled || token !== requestToken) return;
        setContext(null);
        setError('No email item selected. Open an email to use AIRA.');
        setLoading(false);
        return;
      }

      const sender = item.from ?? item.sender;

      item.body.getAsync(Office.CoercionType.Html, (bodyResult) => {
        if (cancelled || token !== requestToken) {
          logger.info('[email-context] loadItem stale callback dropped', {
            trigger,
            itemId: item?.itemId ?? null,
          });
          return;
        }
        if (bodyResult.status === Office.AsyncResultStatus.Failed) {
          setError(bodyResult.error.message);
          setLoading(false);
          return;
        }

        setError(null);
        setContext({
          subject: item.subject ?? '',
          body: cleanEmailBody(htmlBodyToText(bodyResult.value ?? '')),
          senderEmail: sender?.emailAddress ?? '',
          senderName: sender?.displayName ?? '',
          messageId: stableMessageKey(item),
          receivedAt: toIsoDate(item.dateTimeCreated),
        });
        setLoading(false);
      });
    }

    function onItemChanged() {
      logger.info('[email-context] ItemChanged event fired');
      loadItem('itemChanged-event');
    }

    loadItem('initial');
    Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, onItemChanged);

    // Backstop for the ItemChanged unreliability documented above: cheaply
    // compare the current item against the last one we read (itemId and
    // subject — confirmed on outlook.live.com that Outlook.com can freeze
    // the *entire* mailbox.item object, not just itemId, for the pane's
    // lifetime; see the reload() docstring above), and re-run loadItem() if
    // Office moved on without ever firing the event.
    const pollId = setInterval(() => {
      const currentItem = Office.context.mailbox.item;
      const currentItemId = currentItem?.itemId ?? null;
      const currentSubject = currentItem?.subject ?? null;
      if (currentItemId !== lastKnownItemId || currentSubject !== lastKnownSubject) {
        loadItem('poll');
      }
    }, ITEM_CHANGE_POLL_MS);

    // The manifest declares Runtime lifetime="long", so this hook's effect
    // (and its captured state) can survive the task pane being closed —
    // Outlook only tears down an *unpinned* pane's UI, not its background
    // runtime. Without pinning (unavailable on Outlook.com, opt-in
    // elsewhere), the pane closes on every message switch by default; when
    // the user reopens it, this may be the SAME persisted session rather
    // than a fresh mount, so the effect above never re-runs and the pane can
    // reappear showing whatever item it last loaded. Reloading on the
    // visibility transition closes that gap on every host, independent of
    // pinning support or ItemChanged/poll reliability.
    function onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      const currentItemId = Office.context.mailbox.item?.itemId ?? null;
      logger.info('[email-context] visibility restored', { currentItemId, lastKnownItemId });
      if (currentItemId !== lastKnownItemId) {
        loadItem('visibility');
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(pollId);
      Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, onItemChanged);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return { context, loading, error, reload };
}
